import { allocationsToDiscounts } from "@/lib/discount-evaluation/allocations"
import type { CouponEvaluation, RulePromotionEvaluation } from "@/lib/discount-evaluation/types"
import {
  LOYALTY_RULE_PROMO_PREFIX,
  assembleBenefitDiscounts,
  benefitItemsAbatement,
  redemptionAllowedAt,
} from "@/lib/loyalty-benefits"
import { buildRewardDiscounts, emptyRewardsState } from "@/lib/loyalty-rewards"
import type { Cart } from "@/types"
import type { Discount } from "@/types/external"
import {
  canRedeemLoyaltyPoints,
  computeLoyaltyDiscount,
  computeLoyaltyPointsUsed,
  computeMaxRedeemableLoyaltyPoints,
  computePayableItemsSubtotal,
  computePixServiceFee,
  selectedEntitlements,
  type CheckoutState,
} from "../state"

/**
 * Instants a loyalty claim must satisfy. Placement is always one of them —
 * that is the clock loyalty validates — and a scheduled order also has to be
 * inside the window at the ready time the customer proposed.
 */
export function benefitClaimInstants(
  state: Pick<CheckoutState, "orderTiming" | "scheduledSlot">,
): Date[] {
  const now = new Date()
  if (state.orderTiming !== "SCHEDULED" || !state.scheduledSlot) return [now]
  const ready = new Date(state.scheduledSlot.readyAt)
  if (Number.isNaN(ready.getTime())) return [now]
  return [now, ready]
}

function pointsRedemptionOpen(state: CheckoutState, at: readonly Date[]): boolean {
  const availability = state.benefits.active?.pointsProgram?.availability
  return at.every((instant) => redemptionAllowedAt(availability, instant))
}

/**
 * The server's answers this pricing folds in, already matched to the current
 * bag by the caller. Absent means "no answer for this bag yet": its discounts
 * count as zero rather than as a stale amount.
 */
export type ServerDiscounts = {
  rulePromotions?: RulePromotionEvaluation | null
  coupons?: CouponEvaluation | null
}

export type CheckoutPricing = {
  /** Open Delivery discount entries for the loyalty benefits on this order (rewards and rule promotions included). */
  benefitDiscounts: Discount[]
  /** Everything the benefits take off the order (items and delivery fee). */
  benefitTotal: number
  /** The points redemptions (`LOYALTY_REWARD:*`) inside benefitDiscounts, in BRL. */
  rewardTotal: number
  /** Items subtotal every discount is measured against — gift lines excluded. */
  payableItems: number
  /** One discount per coupon allocation coupon-service returned (contract C6). */
  couponDiscounts: Discount[]
  couponDiscount: number
  canRedeemPoints: boolean
  maxRedeemablePoints: number
  pointsUsed: number
  loyaltyDiscount: number
  /** What the customer pays: items + fees − coupon − points − benefits. */
  total: number
}

function sumValues(discounts: readonly Discount[]): number {
  return discounts.reduce((sum, discount) => sum + discount.amount.value, 0)
}

/**
 * Every money figure the checkout screens show, derived in one place.
 *
 * The three screens that quote a total (payment, extras, review) used to each
 * assemble the benefit discounts and then measure points against the raw bag
 * total. That double-counted a free-product reward: its line is billed at full
 * price and cancelled by its own ITEM discount, so it inflated the points cap
 * while costing the customer nothing. Deriving everything from the same
 * assembled discounts keeps each discount on what the order actually charges.
 * Coupons and rule promotions are never computed here: their amounts are the
 * allocations the server returned for this bag, mapped one to one.
 */
export function deriveCheckoutPricing(
  state: CheckoutState,
  cart: Cart,
  server: ServerDiscounts = {},
): CheckoutPricing {
  const at = benefitClaimInstants(state)
  const rulePromotionDiscounts = allocationsToDiscounts(
    (server.rulePromotions?.allocations ?? []).filter((allocation) =>
      allocation.couponCode.startsWith(LOYALTY_RULE_PROMO_PREFIX),
    ),
  )
  const assembled = assembleBenefitDiscounts({
    active: state.benefits.active,
    selectedEntitlements: selectedEntitlements(state),
    servicePrice: state.servicePrice,
    cart,
    at,
    rulePromotionDiscounts,
  })
  const rewardDiscounts = buildRewardDiscounts(state.rewards ?? emptyRewardsState, cart)
  const discounts = [...assembled.discounts, ...rewardDiscounts]
  const rewardTotal = sumValues(rewardDiscounts)
  const totalValue = assembled.totalValue + rewardTotal

  const payableItems = computePayableItemsSubtotal(
    cart.totalPrice,
    benefitItemsAbatement(discounts),
  )
  const pointsOpen = pointsRedemptionOpen(state, at)
  const couponDiscounts = allocationsToDiscounts(server.coupons?.allocations ?? [])
  const couponDiscount = sumValues(couponDiscounts)
  const loyaltyDiscount = pointsOpen ? computeLoyaltyDiscount(state, payableItems) : 0

  return {
    benefitDiscounts: discounts,
    benefitTotal: totalValue,
    rewardTotal,
    payableItems,
    couponDiscounts,
    couponDiscount,
    canRedeemPoints: pointsOpen && canRedeemLoyaltyPoints(state, payableItems),
    maxRedeemablePoints: computeMaxRedeemableLoyaltyPoints(state, payableItems),
    pointsUsed: pointsOpen ? computeLoyaltyPointsUsed(state, payableItems) : 0,
    loyaltyDiscount,
    total: Math.max(
      0,
      cart.totalPrice +
        state.servicePrice +
        computePixServiceFee(state) -
        couponDiscount -
        loyaltyDiscount -
        totalValue,
    ),
  }
}
